import type { StatusSegment } from "../StatusBreakdownChart";
import { splitBySharePrefix } from "./foldSegments";
import { PERSONAL_BUCKET } from "./monitoringScope";

// One key for every "N more spaces" roll-up across the monitoring charts, so a
// panel can treat it uniformly (skip navigation, skip the legend selector, …).
export const OTHER_SPACES_KEY = "__other_spaces__";

// Default fold thresholds for the linear per-space charts (donut arcs, bars,
// stacked bar): a space keeps its own mark only above ~3% of the total, capped
// at 8 to track the categorical palette. Denser visuals (the treemap) pass their
// own thresholds.
export const SPACE_FOLD = { minShare: 0.03, maxKept: 8 };

export type SpaceSegmentRow = { id: string; label: string; value: number };

export function resolveFoldedSpaceIds(
  rows: readonly SpaceSegmentRow[],
  segments: readonly StatusSegment[],
  personalIds: ReadonlySet<string>
): Set<string> {
  const segmentIds = new Set(segments.map((segment) => segment.key));
  const foldedSpaceIds = new Set<string>();
  for (const row of rows) {
    if (segmentIds.has(row.id)) continue;
    if (row.id === PERSONAL_BUCKET) {
      for (const id of personalIds) foldedSpaceIds.add(id);
    } else {
      foldedSpaceIds.add(row.id);
    }
  }
  return foldedSpaceIds;
}

// Ranks spaces and rolls the small-value tail into one grey "Other" segment
// whose details list the folded spaces, so a tenant with many tiny spaces stays
// legible. `rows` must be sorted by value desc. `colorMap` supplies each kept
// space's colour (the Other bucket is always grey, reserved for aggregates).
export function buildSpaceSegments(
  rows: readonly SpaceSegmentRow[],
  total: number,
  colorMap: ReadonlyMap<string, string>,
  formatValue: (value: number) => string = String,
  fold: { minShare: number; maxKept: number } = SPACE_FOLD
): StatusSegment[] {
  const { kept, folded } = splitBySharePrefix(
    rows,
    (row) => row.value,
    total,
    fold
  );
  const segments: StatusSegment[] = kept.map((row) => ({
    key: row.id,
    count: row.value,
    color: colorMap.get(row.id)!,
    label: row.label,
  }));
  if (folded.length > 0) {
    segments.push({
      key: OTHER_SPACES_KEY,
      count: folded.reduce((sum, row) => sum + row.value, 0),
      color: "var(--gray-600)",
      label: `${folded.length} more spaces`,
      details: folded.map((row) => ({
        label: row.label,
        value: formatValue(row.value),
      })),
    });
  }
  return segments;
}
